/**
 * PWWCapital — Error Fallback UI (Section 30)
 *
 * Renders when ErrorBoundary catches an unhandled error.
 * Shows human-readable copy (never raw error text to end users).
 * In development only: displays the error message for debugging.
 */
import React from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import './ErrorFallback.css';

const IS_DEV = process.env.NODE_ENV === 'development';

/**
 * @param {{ error: Error | null; onReset: () => void }} props
 */
function ErrorFallback({ error, onReset }) {
  const handleGoHome = () => {
    onReset?.();
    window.location.replace('/');
  };

  return (
    <div className="error-fallback" role="alert" aria-live="assertive">
      <div className="error-fallback-card">
        <div className="error-fallback-icon" aria-hidden="true">
          <FaExclamationTriangle />
        </div>

        <h1 className="error-fallback-title">Something went wrong</h1>

        <p className="error-fallback-message">
          An unexpected error occurred. Your data is safe and we've been
          notified. Please try again — if the problem continues, contact support.
        </p>

        <div className="error-fallback-actions">
          <button
            className="error-fallback-btn error-fallback-btn--primary"
            onClick={onReset}
            type="button"
          >
            Try again
          </button>

          <button
            className="error-fallback-btn error-fallback-btn--ghost"
            onClick={handleGoHome}
            type="button"
          >
            Go to Dashboard
          </button>
        </div>

        {/* Development-only diagnostic info — never shown in production */}
        {IS_DEV && error?.message && (
          <pre className="error-fallback-code" aria-label="Error details">
            {error.message}
          </pre>
        )}

        <p className="error-fallback-brand">PWW Capital Banking — Secure Banking Platform</p>
      </div>
    </div>
  );
}

export default ErrorFallback;
